import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Card } from "@vb/ui";
import type { BadgeKind } from "@vb/ui";
import { Seo } from "../lib/seo.js";
import { Section, SectionHeading } from "../components/Section.js";
import { track } from "../lib/analytics.js";

const STORAGE_KEY = "vb_operator_key";

const API_HOST: string =
  ((import.meta as unknown as { env: { VITE_API_HOST?: string } }).env.VITE_API_HOST ??
    "https://vaultbreaker-api.fly.dev").replace(/\/$/, "");

/** Mirror of the `/public/operator/me` response from the game-server. We keep
 *  this loose (everything optional except the discriminator fields) because
 *  Agent 8's contract is still settling and the public surface intentionally
 *  drops fields when there's nothing meaningful to surface. */
interface PayoutWalletDTO {
  chain: string;
  currency: string;
  address: string;
}

interface SettlementDTO {
  date: string; // ISO8601
  amount: number;
  currency: string;
}

interface OperatorMe {
  operatorId: string;
  status: "active" | "suspended" | "expired";
  isSandbox: boolean;
  expiresAt?: string;
  rounds30d: number;
  ggr30d: number;
  ggrCurrency?: string;
  lastSettlement?: SettlementDTO;
  payoutWallet?: PayoutWalletDTO;
}

type LoadState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "success"; data: OperatorMe };

function readStoredKey(): string {
  try {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem(STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

function writeStoredKey(value: string): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* private mode / SSR — silently no-op */
  }
}

function clearStoredKey(): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function maskKey(key: string): string {
  if (key.length <= 10) return key.replace(/./g, "•");
  return `${key.slice(0, 6)}…${key.slice(-4)}`;
}

function maskAddress(addr: string): string {
  if (addr.length <= 12) return addr;
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

function formatCurrency(amount: number, currency = "USD"): string {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return iso;
  }
}

function statusBadgeKind(status: OperatorMe["status"]): BadgeKind {
  if (status === "active") return "win";
  if (status === "suspended") return "bust";
  return "fair"; // expired
}

export function Operator() {
  const [apiKey, setApiKey] = useState<string>(() => readStoredKey());
  const [draftKey, setDraftKey] = useState<string>("");
  const [load, setLoad] = useState<LoadState>({ kind: "idle" });

  // On mount, if a key is already persisted, attempt an auto-load. This lets
  // returning operators see their dashboard without re-pasting the key.
  useEffect(() => {
    const stored = readStoredKey();
    if (stored && stored.length > 8) {
      setApiKey(stored);
      void fetchOperator(stored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchOperator(key: string): Promise<void> {
    setLoad({ kind: "loading" });
    try {
      const url = `${API_HOST}/public/operator/me`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          "X-API-Key": key,
          accept: "application/json",
        },
      });
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          setLoad({
            kind: "error",
            message: "API key not recognized. Double-check it or paste a fresh one.",
          });
          return;
        }
        if (res.status === 404) {
          setLoad({ kind: "error", message: "Operator not found." });
          return;
        }
        setLoad({
          kind: "error",
          message: `Server returned ${res.status}. Try again in a moment.`,
        });
        return;
      }
      const data = (await res.json()) as OperatorMe;
      setLoad({ kind: "success", data });
      track("operator_console_login", { isSandbox: data.isSandbox });
    } catch (err) {
      setLoad({
        kind: "error",
        message: err instanceof Error ? err.message : "Network error",
      });
    }
  }

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const trimmed = draftKey.trim();
    if (trimmed.length < 8) {
      setLoad({ kind: "error", message: "That doesn't look like a valid API key." });
      return;
    }
    writeStoredKey(trimmed);
    setApiKey(trimmed);
    setDraftKey("");
    void fetchOperator(trimmed);
  }

  function onLogout(): void {
    clearStoredKey();
    setApiKey("");
    setDraftKey("");
    setLoad({ kind: "idle" });
  }

  return (
    <>
      <Seo
        title="Operator console · Vault Breaker Studios"
        description="Operator console — read-only insight into your Vault Breaker integration."
        path="/operator"
      />

      <Section>
        <Badge kind="fair" style={{ marginBottom: 18 }}>
          OPERATOR CONSOLE
        </Badge>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(40px, 5.5vw, 64px)",
            lineHeight: 1,
            margin: 0,
            color: "var(--fg-1)",
            textTransform: "uppercase",
          }}
        >
          Operator console
        </h1>
        <p
          style={{
            fontSize: 17,
            color: "var(--fg-2)",
            marginTop: 14,
            maxWidth: 720,
            lineHeight: 1.55,
          }}
        >
          Read-only insight into your integration. Paste your API key to see status, 30-day
          volume, last settlement, and payout wallet on file.
        </p>
      </Section>

      {!apiKey || load.kind === "idle" || load.kind === "error" || load.kind === "loading" ? (
        <Section style={{ paddingTop: 0, maxWidth: 640 }}>
          <Card style={{ padding: 24 }}>
            <form onSubmit={onSubmit} style={{ display: "grid", gap: 14 }}>
              <label style={{ display: "grid", gap: 6 }}>
                <span className="label" style={{ color: "var(--fg-2)" }}>
                  API key
                </span>
                <input
                  type="password"
                  className="vb-text-input"
                  value={draftKey}
                  onChange={(e) => setDraftKey(e.target.value)}
                  placeholder="vb_live_… or vb_sbx_…"
                  autoComplete="off"
                  spellCheck={false}
                  required
                  minLength={8}
                  style={{ fontFamily: "var(--font-mono)", fontSize: 13 }}
                />
                <span style={{ fontSize: 11, color: "var(--fg-3)" }}>
                  Stored locally in your browser only — never sent anywhere except{" "}
                  <code className="mono">{API_HOST}</code>.
                </span>
              </label>

              {load.kind === "error" ? (
                <div
                  style={{
                    padding: "12px 14px",
                    border: "1px solid rgba(255,59,71,0.35)",
                    background: "var(--red-wash)",
                    color: "var(--red)",
                    fontSize: 13,
                    borderRadius: 10,
                  }}
                >
                  {load.message}
                </div>
              ) : null}

              <div style={{ display: "flex", gap: 10, justifyContent: "space-between" }}>
                <span style={{ fontSize: 11, color: "var(--fg-3)", fontFamily: "var(--font-mono)" }}>
                  No account yet? <Link to="/sandbox" style={{ color: "var(--cyan)" }}>Get a sandbox key →</Link>
                </span>
                <Button
                  variant="gold"
                  type="submit"
                  disabled={load.kind === "loading"}
                  style={{ padding: "12px 18px", fontSize: 14 }}
                >
                  {load.kind === "loading" ? "LOADING…" : "OPEN CONSOLE"}
                </Button>
              </div>
            </form>
          </Card>
        </Section>
      ) : null}

      {load.kind === "success" ? (
        <SuccessView data={load.data} apiKey={apiKey} onLogout={onLogout} />
      ) : null}
    </>
  );
}

function SuccessView({
  data,
  apiKey,
  onLogout,
}: {
  data: OperatorMe;
  apiKey: string;
  onLogout: () => void;
}) {
  const ggrCurrency = data.ggrCurrency ?? "USD";
  return (
    <>
      <Section style={{ paddingTop: 0 }}>
        <SectionHeading
          eyebrow="Account"
          title="Your integration"
          lead="Numbers are pulled live from the game-server. Some fields appear only after first activity."
        />

        <Card style={{ padding: 0, overflow: "hidden" }}>
          <Row label="Operator ID">
            <span className="mono" style={{ fontFamily: "var(--font-mono)", color: "var(--fg-1)" }}>
              {data.operatorId}
            </span>
          </Row>
          <Row label="Status">
            <Badge kind={statusBadgeKind(data.status)}>{data.status.toUpperCase()}</Badge>
          </Row>
          <Row label="Environment">
            <Badge kind={data.isSandbox ? "fair" : "win"}>
              {data.isSandbox ? "SANDBOX" : "PRODUCTION"}
            </Badge>
          </Row>
          {data.isSandbox && data.expiresAt ? (
            <Row label="Expires">
              <span style={{ color: "var(--fg-2)" }}>{formatDate(data.expiresAt)}</span>
            </Row>
          ) : null}
          <Row label="Active key">
            <span className="mono" style={{ fontFamily: "var(--font-mono)", color: "var(--fg-3)" }}>
              {maskKey(apiKey)}
            </span>
          </Row>
        </Card>
      </Section>

      <Section style={{ paddingTop: 0 }}>
        <SectionHeading eyebrow="Volume" title="Last 30 days" />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 14,
          }}
        >
          <Card style={{ padding: 22, display: "grid", gap: 6 }}>
            <span className="label" style={{ color: "var(--fg-3)" }}>
              Rounds (30d)
            </span>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 28,
                color: "var(--fg-1)",
                letterSpacing: "0.02em",
              }}
            >
              {data.rounds30d.toLocaleString("en-US")}
            </span>
          </Card>
          <Card style={{ padding: 22, display: "grid", gap: 6 }}>
            <span className="label" style={{ color: "var(--fg-3)" }}>
              GGR (30d)
            </span>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 28,
                color: "var(--gold-bright)",
                letterSpacing: "0.02em",
              }}
            >
              {formatCurrency(data.ggr30d, ggrCurrency)}
            </span>
          </Card>
        </div>
      </Section>

      <Section style={{ paddingTop: 0 }}>
        <SectionHeading eyebrow="Settlements" title="Last payout" />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {data.lastSettlement ? (
            <>
              <Row label="Date">
                <span style={{ color: "var(--fg-2)" }}>
                  {formatDate(data.lastSettlement.date)}
                </span>
              </Row>
              <Row label="Amount">
                <span
                  className="mono"
                  style={{ fontFamily: "var(--font-mono)", color: "var(--fg-1)" }}
                >
                  {formatCurrency(data.lastSettlement.amount, data.lastSettlement.currency)}
                </span>
              </Row>
              <Row label="Currency">
                <span style={{ color: "var(--fg-2)" }}>{data.lastSettlement.currency}</span>
              </Row>
            </>
          ) : (
            <Row label="History">
              <span style={{ color: "var(--fg-3)" }}>
                No settlements yet — first one fires after your first weekly cycle.
              </span>
            </Row>
          )}
        </Card>
      </Section>

      <Section style={{ paddingTop: 0 }}>
        <SectionHeading eyebrow="Payout wallet" title="On file" />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {data.payoutWallet ? (
            <>
              <Row label="Chain">
                <span style={{ color: "var(--fg-2)" }}>{data.payoutWallet.chain}</span>
              </Row>
              <Row label="Currency">
                <span style={{ color: "var(--fg-2)" }}>{data.payoutWallet.currency}</span>
              </Row>
              <Row label="Address">
                <span
                  className="mono"
                  style={{ fontFamily: "var(--font-mono)", color: "var(--fg-3)" }}
                  title={data.payoutWallet.address}
                >
                  {maskAddress(data.payoutWallet.address)}
                </span>
              </Row>
            </>
          ) : (
            <Row label="Status">
              <span style={{ color: "var(--fg-3)" }}>
                No payout wallet on file. Email{" "}
                <a href="mailto:hello@vaultbreaker.dev" style={{ color: "var(--cyan)" }}>
                  hello@vaultbreaker.dev
                </a>{" "}
                to set one up.
              </span>
            </Row>
          )}
        </Card>
      </Section>

      <Section style={{ paddingTop: 0 }}>
        <Card style={{ padding: 22, display: "grid", gap: 14 }}>
          <SectionHeading eyebrow="Actions" title="Account controls" />
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Button
              variant="ghost"
              disabled
              title="Self-rotate ships in the next admin API release."
              style={{ padding: "12px 18px", fontSize: 14 }}
              // TODO: wire to admin API once self-rotate endpoint ships
            >
              ROTATE API KEY
            </Button>
            <Link to="/integration" style={{ textDecoration: "none" }}>
              <Button style={{ padding: "12px 18px", fontSize: 14 }}>
                VIEW INTEGRATION DOCS →
              </Button>
            </Link>
            <Button
              variant="danger"
              onClick={onLogout}
              style={{ padding: "12px 18px", fontSize: 14 }}
            >
              LOGOUT
            </Button>
          </div>
          <span style={{ fontSize: 11, color: "var(--fg-3)", fontFamily: "var(--font-mono)" }}>
            Logout clears the key from this browser only — it does not invalidate the key
            server-side.
          </span>
        </Card>
      </Section>
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: "14px 22px",
        borderTop: "1px solid var(--line)",
        display: "grid",
        gridTemplateColumns: "minmax(140px, 200px) 1fr",
        gap: 16,
        alignItems: "center",
      }}
    >
      <span className="label" style={{ color: "var(--fg-3)" }}>
        {label}
      </span>
      <span>{children}</span>
    </div>
  );
}
